import React, { useEffect, useLayoutEffect } from 'react';
import {
  DrawingFetchRequestParams,
  DrawingType,
  DrawingUpdateDetails,
  DropLocationDrawing,
  OverlayTools,
} from './constants';
import { WellDetailsStates } from 'business/models/CustomModels';
import { useRMDispatch } from 'redux/hooks';
import { fetchDrawingsThunk, updateDrawingThunk } from 'redux/thunks/well';
import {
  useWellDrawings,
  useSelectedWellDrawing,
  useSelectedDrawingTool,
} from 'redux/hooks/well';
import { Rect } from 'react-konva';
import DrawingRenderer from './drawingRenderer';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import {
  adjustDrawingsPositions,
  setDrawingTool,
  setDropLocation,
  setSelectedDrawing,
  unSelectDrawing,
  updateDrawing,
} from 'redux/reducers/wellReducer';
import { DropDetails } from '../constants';
import { getNewId } from 'utils/Global';

interface Props {
  wellDetails: DropDetails | WellDetailsStates;
  tpObject: TransformPoints;
  zoomScale: number;
  canvasWidth: number;
  canvasHeight: number;
  regionName?: string;
  hideDrawings?: boolean;
}
const DrawingManager = (props: Props) => {
  const {
    wellDetails,
    tpObject,
    zoomScale,
    canvasWidth,
    canvasHeight,
    regionName: regionNameProp,
    hideDrawings = false,
  } = props;
  const dispatch = useRMDispatch();
  const {
    plateID,
    wellNum,
    dropNumber,
    cpID,
    regionName: _regionName,
  } = wellDetails || {};
  const imageBatchId =
    typeof wellDetails === 'object' && 'inspectionSet' in wellDetails
      ? wellDetails.inspectionSet.groupedInspections.find(
          (insp) => insp.plateId == plateID,
        )?.imageBatchId
      : wellDetails?.imageBatchId;
  const regionName = _regionName || regionNameProp;
  const wellDrawings = useWellDrawings(plateID, wellNum, dropNumber, regionName);
  const selectedDrawing = useSelectedWellDrawing();
  const selectedDrawingTool = useSelectedDrawingTool();
  const { drawings, canvasSize, status } = wellDrawings || {};
  const {
    annotations,
    crystals,
    pois,
    rois,
    rulers,
    scribbles,
    lightPaths,
    dropLocations,
  } = drawings || {};

  const updateDrawingAction = (
    drawingId: number,
    drawingType: DrawingType,
    drawingUpdateDetails: DrawingUpdateDetails,
  ) => {
    dispatch(
      updateDrawing({
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName,
        drawingType: drawingType,
        id: drawingId,
        drawingUpdateDetails,
      }),
    );
  };

  const saveAndUpdateDrawingAction = (
    drawingId: number,
    drawingType: DrawingType,
    drawingUpdateDetails: DrawingUpdateDetails,
  ) => {
    dispatch(
      updateDrawingThunk({
        drawingId: drawingId,
        drawingType: drawingType,
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName,
        drawingUpdateDetails,
        tpObject,
      }),
    );
  };

  const selectDrawing = (drawingId: number, drawingType: DrawingType) => {
    dispatch(
      setSelectedDrawing({
        id: drawingId,
        drawingType: drawingType,
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        regionName,
      }),
    );
  };

  const selectDrawingTool = (drawingTool: OverlayTools) => {
    dispatch(setDrawingTool(drawingTool));
  };
  useEffect(() => {
    if (wellDetails && status === undefined && tpObject && regionName) {
      const fetchDrawingRequest: DrawingFetchRequestParams = {
        plateId: plateID,
        wellNumber: wellNum,
        dropNumber: dropNumber,
        batchId: imageBatchId,
        captureProfileId: cpID,
        regionName: regionName,
        tpObject,
      };
      dispatch(fetchDrawingsThunk(fetchDrawingRequest));
    }
  }, [wellDetails, regionName, tpObject]);

  useEffect(() => {
    if (selectedDrawingTool === OverlayTools.DROPLOCATION) {
      const height = tpObject.canvasHeight / 2;
      const width = tpObject.canvasWidth / 2;
      const x = width / 2;
      const y = height / 2;
      const {
        x: dbXCenterOffset,
        y: dbYCenterOffset,
        width: dbWidth,
        height: dbHeight,
      } = tpObject.convertRectangleDrawingForDb(x, y, width, height);
      const dropLocation: DropLocationDrawing = {
        id: getNewId(),
        locked: false,
        x,
        y,
        width,
        height,
        dbWidth,
        dbHeight,
        dbXCenterOffset,
        dbYCenterOffset,
        zIndex: 0,
      };
      dispatch(
        setDropLocation({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber: dropNumber,
          drawing: dropLocation,
          regionName,
        }),
      );
      dispatch(setDrawingTool(OverlayTools.POINTER));
    }
  }, [selectedDrawingTool]);

  useLayoutEffect(() => {
    if (
      canvasSize &&
      tpObject &&
      (canvasSize?.canvasHeight !== tpObject?.canvasHeight ||
        canvasSize?.canvasWidth !== tpObject?.canvasWidth)
    ) {
      dispatch(
        adjustDrawingsPositions({
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber: dropNumber,
          regionName,
          tpObject,
        }),
      );
    }
  }, [tpObject?.canvasWidth, tpObject?.canvasHeight]);
  return (
    <>
      <Rect
        id="drawingmanager-rect-background"
        x={0}
        y={0}
        width={canvasWidth}
        height={canvasHeight}
        onClick={() => dispatch(unSelectDrawing())}
      />
      <DrawingRenderer
        selectedDrawing={selectedDrawing}
        annotations={annotations}
        crystals={crystals}
        pois={pois}
        rois={rois}
        rulers={rulers}
        scribbles={scribbles}
        lightPaths={lightPaths}
        dropLocations={dropLocations}
        tpObject={tpObject}
        updateDrawing={updateDrawingAction}
        saveAndUpdateDrawing={saveAndUpdateDrawingAction}
        selectDrawing={selectDrawing}
        zoomScale={zoomScale}
        regionName={regionName}
        selectedDrawingTool={selectedDrawingTool}
        selectDrawingTool={selectDrawingTool}
        hideDrawings={hideDrawings}
      />
    </>
  );
};

export default DrawingManager;
